import { ENV } from '~constants/environments'

export type GeocodedAddress = {
  city?: string
  state?: string
  country?: string
  countryCode?: string
  postalCode?: string
}

type GoogleMapsGeocodingResult = {
  address_components?: {
    long_name: string
    short_name: string
    types: string[]
  }[]
  formatted_address?: string
  geometry?: {
    location?: {
      lat: number
      lng: number
    }
  }
}

type GoogleMapsGeocodingResponse = {
  results?: GoogleMapsGeocodingResult[]
  status?: string
}

const GOOGLE_MAPS_GEOCODING_URL = 'https://maps.googleapis.com/maps/api/geocode/json'

const extractAddressComponent = (
  components: GoogleMapsGeocodingResult['address_components'],
  type: string,
  preferShortName = false
): string | undefined => {
  if (!components) return undefined
  const component = components.find((comp) => comp.types.includes(type))
  if (!component) return undefined
  return preferShortName ? component.short_name || component.long_name : component.long_name || component.short_name
}

/**
 * Get address information by postal code using Google Maps Geocoding API.
 * Uses only the postal_code component so the correct country/city/state is returned.
 */
export const getAddressByPostalCode = async (
  postalCode: string,
  signal?: AbortSignal
): Promise<GeocodedAddress | null> => {
  if (!ENV.GOOGLE_MAPS_API_KEY) {
    return null
  }

  try {
    const searchParams = new URLSearchParams({
      address: postalCode,
      components: `postal_code:${postalCode}`,
      key: ENV.GOOGLE_MAPS_API_KEY,
    })

    const response = await fetch(`${GOOGLE_MAPS_GEOCODING_URL}?${searchParams.toString()}`, {
      signal,
    })
    if (!response.ok) {
      return null
    }

    const data = (await response.json()) as GoogleMapsGeocodingResponse
    if (data.status !== 'OK' || !data.results?.[0]) {
      return null
    }

    const result = data.results[0]
    const components = result.address_components

    if (!components) {
      return null
    }

    // Extract city (locality or administrative_area_level_2)
    const city =
      extractAddressComponent(components, 'locality') ||
      extractAddressComponent(components, 'administrative_area_level_2') ||
      extractAddressComponent(components, 'sublocality') ||
      extractAddressComponent(components, 'sublocality_level_1')

    // Extract state (administrative_area_level_1)
    const state =
      extractAddressComponent(components, 'administrative_area_level_1') ||
      extractAddressComponent(components, 'administrative_area_level_2')

    // Extract country
    const country = extractAddressComponent(components, 'country')
    const countryCode = extractAddressComponent(components, 'country', true)?.toUpperCase()

    // Extract postal code
    const postalCodeResult =
      extractAddressComponent(components, 'postal_code') ||
      extractAddressComponent(components, 'postal_code_prefix')

    return {
      city,
      state,
      country,
      countryCode,
      postalCode: postalCodeResult,
    }
  } catch (error) {
    // Silently handle abort errors
    if ((error as Error).name !== 'AbortError') {
      // Google Maps geocoding request failed
    }
    return null
  }
}

/**
 * Get address information by coordinates (reverse geocoding) using Google Maps Geocoding API.
 * Uses latitude and longitude only so the actual location/country is returned (no country filter).
 */
export const getAddressByCoordinates = async (
  latitude: number,
  longitude: number,
  signal?: AbortSignal
): Promise<GeocodedAddress | null> => {
  if (!ENV.GOOGLE_MAPS_API_KEY) {
    return null
  }

  try {
    const searchParams = new URLSearchParams({
      latlng: `${latitude},${longitude}`,
      key: ENV.GOOGLE_MAPS_API_KEY,
    })

    const response = await fetch(`${GOOGLE_MAPS_GEOCODING_URL}?${searchParams.toString()}`, {
      signal,
    })
    if (!response.ok) {
      return null
    }

    const data = (await response.json()) as GoogleMapsGeocodingResponse
    if (data.status !== 'OK' || !data.results?.[0]) {
      return null
    }

    const result = data.results[0]
    const components = result.address_components

    if (!components) {
      return null
    }

    // Extract city (locality or administrative_area_level_2)
    const city =
      extractAddressComponent(components, 'locality') ||
      extractAddressComponent(components, 'administrative_area_level_2') ||
      extractAddressComponent(components, 'sublocality') ||
      extractAddressComponent(components, 'sublocality_level_1')

    // Extract state (administrative_area_level_1)
    const state =
      extractAddressComponent(components, 'administrative_area_level_1') ||
      extractAddressComponent(components, 'administrative_area_level_2')

    // Extract country
    const country = extractAddressComponent(components, 'country')
    const countryCode = extractAddressComponent(components, 'country', true)?.toUpperCase()

    // Extract postal code
    const postalCode =
      extractAddressComponent(components, 'postal_code') ||
      extractAddressComponent(components, 'postal_code_prefix')

    return {
      city,
      state,
      country,
      countryCode,
      postalCode,
    }
  } catch (error) {
    // Silently handle abort errors
    if ((error as Error).name !== 'AbortError') {
      // Google Maps reverse geocoding request failed
    }
    return null
  }
}
